import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import { requestApi } from 'api/Api';

import { usePractitioners } from 'lib/hooks/queries/practitioners/usePractitioners';

import { showAlert } from 'components/shared/Alert/Alert';
import Button from 'components/shared/Buttons/Button';
import Icon from 'components/shared/Icon/Icon';
import Select from 'components/shared/Select/Select';

import IdleWrapper from '../components/IdleWrapper';
import RequestAssistance from '../components/RequestAssistance';

const ScheduleAppointment = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const patientId = location.state?.patientId;
  const patientName = location.state?.patientName || 'Patient';

  const { data: practitionersData, isLoading: practitionersLoading } = usePractitioners();

  const practitioners = practitionersData?.practitionersOptions || [];

  const formik = useFormik({
    initialValues: { practitionerId: '' },
    validationSchema: Yup.object({
      practitionerId: Yup.string().required('Practitioner is required')
    }),
    onSubmit: async (values) => {
      if (!patientId) {
        showAlert({ title: 'Patient information is missing', color: 'danger' });
        return;
      }

      setLoading(true);

      const appointmentToBeCreated = {
        user_id: patientId,
        practitioner_id: values.practitionerId,
        resource_id: null,
        practice_note: null,
        type: null,
        starts_at: new Date().toISOString(),
        ends_at: new Date(new Date().getTime() + 60 * 60000).toISOString(), // 1 hour appointment
        location: null,
        form_ids: [],
        procedures: null,
        products: null,
        virtual_link: null,
        enable_notifications: true,
        tag_ids: null
      };

      await requestApi({
        url: '/api/appointment/create',
        params: {
          appointment: appointmentToBeCreated,
          appointmentLength: 60,
          checkForExistingAppt: false,
          selfRegistration: true
        },
        onSuccess: async (data) => {
          if (data?.appointment_id) {
            showAlert({ title: 'Appointment scheduled successfully', color: 'success' });

            const appointmentId = data?.appointment_id;

            await requestApi({
              url: `/api/patient/checkin/start`,
              params: { appointmentId, patientImpersonate: true },
              onSuccess: (checkinData) => {
                localStorage.setItem('appointmentId', appointmentId);
                if (checkinData?.redirectTo) {
                  navigate(checkinData.redirectTo);
                } else {
                  showAlert({ title: 'Failed to start check-in process', color: 'danger' });
                }
              },
              onError: () => {
                showAlert({ title: 'Failed to start check-in process', color: 'danger' });
              },
              navigate
            });
          }
        },
        onError: () => {
          showAlert({ title: 'Failed to schedule appointment', color: 'danger' });
        },
        navigate
      });

      setLoading(false);
    }
  });

  return (
    <IdleWrapper>
      <div className="rounded-t-2xl bg-primary-50 !px-8 !py-3 xs:!px-4">
        <div className="flex items-center justify-between gap-4">
          <p className="text-lg font-600 leading-7 text-primary-900">Schedule Appointment</p>
          <div
            className="ml-auto flex cursor-pointer select-none gap-1"
            onClick={() => navigate('../idle')}>
            <Icon
              size={20}
              stroke
              color="primary"
              shade={900}
              icon="new-arrow-left"
              className="cursor-pointer"
            />
            <p className="text-base font-600 leading-7 text-primary-900">Go back</p>
          </div>
        </div>
        <p className="font-regular mt-1 text-base text-neutral-600">
          Hi {patientName}, please select a practitioner to schedule your appointment
        </p>
      </div>
      <div className="!px-8 !py-4 xs:!px-4 xs:!pb-3 xs:!pt-2">
        <div className="mb-4 grid grid-cols-1">
          <Select
            label="Practitioner"
            required
            placeholder="Type and select a practitioner"
            value={practitioners.find(
              (practitioner) => practitioner?.value === formik.values.practitionerId
            )}
            onChange={(value) => {
              formik.setFieldValue('practitionerId', value?.value);
            }}
            options={practitioners}
            inputId="practitionerSelect"
            error={formik.errors.practitionerId}
            isImage
            className="!mt-2"
            isLoading={practitionersLoading}
          />
        </div>

        <Button
          size="large"
          text="Schedule Appointment"
          className="!mt-4 w-full"
          loading={loading}
          onClick={() => formik.submitForm()}
        />

        <div className="!mt-4 flex select-none flex-wrap items-center justify-between gap-x-6 gap-y-2">
          <RequestAssistance page="schedule-appointment" />
          <div className="ml-auto flex items-center gap-x-[6px]">
            <p className="text-xs font-400 text-primary-500">Powered by</p>
            <Icon icon="logo-without-symbol" size={76} />
          </div>
        </div>
      </div>
    </IdleWrapper>
  );
};

export default ScheduleAppointment;
